/**
 * Carbon Glance User Matrix — single source of truth for per-page CRUD
 * permissions. Mirrors the Lincoln Electric "Carbon Glance User Matrix" sheet
 * (Carbon Glance User Role tab).
 *
 * Each entry maps a sidebar page → { area, resourceKey, route, sidebarLabel,
 * parentLabel }. The frontend uses Clerk permissions in the form
 *   org:{resourceKey}:read | create | edit | delete
 * Role configuration lives in the Clerk dashboard; this file only declares
 * which permission string each page checks.
 *
 * When the same physical route is shared between areas (e.g. /cost-radar,
 * /carbon-price-tracker), the resourceKey is split per area so a single role
 * can have different CRUD on each area. The PermissionRoute / sidebar pick the
 * right entry from the active org type via `getMatrixEntry(route, area)`.
 */

import { OrganizationType } from 'modules/Common/utils/Utility';

export const AREA = Object.freeze({
  Declarant: OrganizationType.Declarant,
  Operator: OrganizationType.Operator,
});

export const ROLE_SLUGS = Object.freeze({
  Admin: 'org:cg_admin',
  TradeCompliance: 'org:trade_compliance_sme',
  Finance: 'org:finance_sme',
  Sourcing: 'org:sourcing_sme',
  ProductCompliance: 'org:product_compliance_sme',
  Sustainability: 'org:sustainability_sme',
  Guest: 'org:guest',
});

// The four CRUD actions in the matrix. Call sites should use ACTIONS.edit
// rather than the literal string 'edit' so typos surface at parse time and
// renames are a single source of truth.
export const ACTIONS = Object.freeze({
  read: 'read',
  create: 'create',
  edit: 'edit',
  delete: 'delete',
});

const declarantPages = [
  { resourceKey: 'declarant_home', route: '/overview', sidebarLabel: 'Home' },
  {
    resourceKey: 'declarant_upload_hub',
    route: '/upload-hub',
    sidebarLabel: 'Upload Hub',
  },
  {
    resourceKey: 'declarant_download_hub',
    route: '/download-hub',
    sidebarLabel: 'Download Hub',
  },
  {
    resourceKey: 'imports',
    route: '/imports',
    sidebarLabel: 'Imports',
    parentLabel: 'Manage',
  },
  // Purchases (docs/features/purchases PU11). `purchases` is the operator's
  // stock resource, so the declarant one carries the area prefix.
  {
    resourceKey: 'declarant_purchases',
    route: '/purchases',
    sidebarLabel: 'Purchases',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'suppliers',
    route: '/suppliers',
    sidebarLabel: 'Suppliers',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'installations',
    route: '/installations',
    sidebarLabel: 'Installations',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'emissions',
    route: '/emissions',
    sidebarLabel: 'Emissions',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'companies',
    route: '/companies',
    sidebarLabel: 'Companies',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'benchmarks',
    route: '/benchmarks',
    sidebarLabel: 'Benchmarks',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'declarant_overview',
    route: '/analytics-overview',
    sidebarLabel: 'Overview',
    parentLabel: 'Analyze',
  },
  {
    resourceKey: 'declarant_certificate_price',
    route: '/carbon-price-tracker',
    sidebarLabel: 'Certificate Price',
    parentLabel: 'Analyze',
  },
  {
    resourceKey: 'declarant_cost_radar',
    route: '/cost-radar',
    sidebarLabel: 'Cost Radar',
    parentLabel: 'Analyze',
  },
  {
    resourceKey: 'cbam_costs',
    route: '/cbam-costs',
    sidebarLabel: 'Costs',
    parentLabel: 'Analyze',
  },
  {
    resourceKey: 'cbam_quotes',
    route: '/cbam-quotes',
    sidebarLabel: 'Quotes',
    parentLabel: 'Analyze',
  },
  { resourceKey: 'declarant_reports', route: '/reports', sidebarLabel: 'Report' },
].map((page) => ({ ...page, area: AREA.Declarant }));

const operatorPages = [
  { resourceKey: 'operator_home', route: '/operator-overview', sidebarLabel: 'Home' },
  {
    resourceKey: 'operator_upload_hub',
    route: '/stock/upload-hub',
    sidebarLabel: 'Upload Hub',
  },
  {
    resourceKey: 'operator_download_hub',
    route: '/stock/download-hub',
    sidebarLabel: 'Download Hub',
  },
  {
    resourceKey: 'inventory',
    route: '/inventory/lots',
    sidebarLabel: 'Inventory',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'operator_suppliers',
    route: '/stock/suppliers',
    sidebarLabel: 'Suppliers',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'operator_installations',
    route: '/operator-installations',
    sidebarLabel: 'Installations',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'purchases',
    route: '/stock/purchases',
    sidebarLabel: 'Purchases',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'sell_orders',
    route: '/stock/sales',
    sidebarLabel: 'Sales',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'operator_reports',
    route: '/stock/reports',
    sidebarLabel: 'Reports',
    parentLabel: 'Manage',
  },
  {
    resourceKey: 'operator_overview',
    route: '/operator-analytics',
    sidebarLabel: 'Overview',
    parentLabel: 'Analyze',
  },
  {
    resourceKey: 'operator_certificate_price',
    route: '/carbon-price-tracker',
    sidebarLabel: 'Certificate Price',
    parentLabel: 'Analyze',
  },
  {
    resourceKey: 'operator_cost_radar',
    route: '/cost-radar',
    sidebarLabel: 'Cost Radar',
    parentLabel: 'Analyze',
  },
  {
    resourceKey: 'operator_premiums',
    route: '/operator-premiums',
    sidebarLabel: 'Premiums',
    parentLabel: 'Analyze',
  },
  {
    resourceKey: 'connections',
    route: '/connections/connected',
    sidebarLabel: 'Connections',
    parentLabel: 'Connect',
  },
  {
    resourceKey: 'connection_requests',
    route: '/connections/invites',
    sidebarLabel: 'Requests',
    parentLabel: 'Connect',
  },
  { resourceKey: 'verification', route: '/verification', sidebarLabel: 'Verification' },
].map((page) => ({ ...page, area: AREA.Operator }));

export const MATRIX_PAGES = Object.freeze([...declarantPages, ...operatorPages]);

/**
 * Look up a matrix entry by exact route + area. Returns null when the route
 * is not gated by the matrix (default-allow per plan).
 */
export const getMatrixEntry = (route, area) =>
  MATRIX_PAGES.find((page) => page.route === route && page.area === area) || null;

/**
 * Resolve the resourceKey to check for a given (route, area) pair. Returns
 * null if the route is outside the matrix.
 */
export const getResourceForRoute = (route, area) => {
  const entry = getMatrixEntry(route, area);
  return entry ? entry.resourceKey : null;
};

/** All matrix entries for a single area, in sidebar order. */
export const getPagesForArea = (area) => MATRIX_PAGES.filter((page) => page.area === area);
